// Supabase implementation of DialerApi (spec 04): the dialer RPCs of 20261012110000_gavion_dialer_automation.sql.
// The fake API (fakeDialerApi.ts) is the demo/test twin; both follow the same contract (types.ts).
import type { SupabaseClient } from "@supabase/supabase-js";
import type { QueueFilterOption } from "../../domain/dialer/filterOptions";
import type { SalesDialPrefs } from "../provider/selectProvider";
// GAVION contact-call
import {
  contactDisplayName,
  findContactPhone,
} from "../../domain/dialer/contactCall";
import {
  DEFAULT_TZ,
  loadSnapshot,
  loadSnapshots,
  must,
  str,
  type Row,
} from "./supabaseSnapshots";
import {
  DialerApiError,
  type ActivityItem,
  type CalledContact,
  type CallHistoryRow, // GAVION my-calls
  type CallRow,
  type ClaimReason,
  type ClaimResult,
  type DialSession,
  type DialerApi,
  type InboundEndResult, // GAVION inbound calls
  type InboundStartResult, // GAVION inbound calls
  type MyPrefs,
  type TodayStats,
} from "./types";

type ClaimRow = {
  company_id: number | null;
  call_id: number | null;
  reason: ClaimReason;
  warning?: ClaimResult["warning"];
  /** claim_next_lead only (GAVION requeue fix, 49): the reused open call's claimed_at. */
  resumed_at?: string | null;
};

/** `sb` may be a getter: the client is then created on the first call (no Supabase env needed to mount). */
export function createSupabaseDialerApi(
  sb: SupabaseClient | (() => SupabaseClient),
  me: number,
  now: () => Date = () => new Date(),
): DialerApi {
  const db = (): SupabaseClient => (typeof sb === "function" ? sb() : sb);
  const rpc = async <T>(fn: string, args?: Row): Promise<T> =>
    must(await db().rpc(fn, args)) as T;
  const firstRow = <T>(data: T[] | T | null): T | null =>
    Array.isArray(data) ? (data[0] ?? null) : data;

  const snapshot = (id: number) => loadSnapshot(db(), id);

  const getActiveSession = async (): Promise<DialSession | null> =>
    must(
      await db()
        .from("dial_sessions")
        .select("*")
        .eq("sales_id", me)
        .is("ended_at", null)
        .maybeSingle(),
    ) as DialSession | null;

  const nextCallback = async (): Promise<string | null> => {
    const row = must(
      await db()
        .from("companies")
        .select("next_callback_at")
        .gt("next_callback_at", now().toISOString())
        .or(`assigned_sales_id.eq.${me},assigned_sales_id.is.null`)
        .order("next_callback_at")
        .limit(1)
        .maybeSingle(),
    ) as Row | null;
    return str(row?.next_callback_at);
  };

  const lockedByName = async (companyId: number): Promise<string | null> => {
    const c = must(
      await db()
        .from("companies")
        .select("locked_by")
        .eq("id", companyId)
        .maybeSingle(),
    ) as Row | null;
    if (!c?.locked_by) return null;
    const holder = must(
      await db()
        .from("sales")
        .select("first_name")
        .eq("id", c.locked_by)
        .maybeSingle(),
    ) as Row | null;
    return str(holder?.first_name);
  };

  const leadTimezone = async (companyId: number): Promise<string> => {
    const c = must(
      await db()
        .from("companies")
        .select("timezone")
        .eq("id", companyId)
        .maybeSingle(),
    ) as Row | null;
    return str(c?.timezone) ?? DEFAULT_TZ;
  };

  /** ClaimRow → ClaimResult: `ok` carries the snapshot, the empty reasons their hint. */
  const toResult = async (
    row: ClaimRow | null,
    sessionId: number,
    /** Zone for the next calling window: the lead's on a manual dial (claim_specific_lead checks it). */
    windowTz: () => Promise<string> = async () => DEFAULT_TZ,
  ): Promise<ClaimResult> => {
    const reason = row?.reason ?? "queue_empty";
    const base: ClaimResult = {
      reason,
      session_id: sessionId,
      company_id: row?.company_id ?? null,
      call_id: row?.call_id ?? null,
      lead: null,
      warning: row?.warning ?? null,
      resumed_at: row?.resumed_at ?? null,
    };
    if (reason === "ok" && row?.company_id != null)
      return {
        ...base,
        lead: await snapshot(row.company_id),
        to_number:
          row.call_id != null ? await claimedNumber(row.call_id) : null,
      };
    if (reason === "outside_calling_hours")
      return {
        ...base,
        next_window: await rpc<string>("next_calling_window", {
          p_ts: now().toISOString(),
          p_tz: await windowTz(),
        }),
      };
    if (reason === "queue_empty")
      return { ...base, next_callback_at: await nextCallback() };
    return base;
  };

  /** Review fixes I1 + M1: the number recorded on the claimed call row, the one the dialer dials. */
  const claimedNumber = async (callId: number): Promise<string | null> => {
    const k = must(
      await db()
        .from("calls")
        .select("to_number")
        .eq("id", callId)
        .maybeSingle(),
    ) as Row | null;
    return str(k?.to_number);
  };

  /** GAVION contact-call: the called contact for the dialer card (null if it vanished meanwhile). */
  const calledContact = async (
    contactId: number,
    phone: string,
  ): Promise<CalledContact | null> => {
    const k = must(
      await db()
        .from("contacts")
        .select("id, first_name, last_name, title, phone_jsonb")
        .eq("id", contactId)
        .maybeSingle(),
    ) as Row | null;
    const match = k ? findContactPhone(k.phone_jsonb, phone) : null;
    if (!k || !match) return null;
    return {
      id: Number(k.id),
      name: contactDisplayName(
        k as { first_name?: string | null; last_name?: string | null },
      ),
      title: str(k.title),
      phone_e164: match.e164,
      phone_type: match.type,
    };
  };

  const api: DialerApi = {
    getActiveSession,

    startSession: (filter) =>
      rpc<DialSession>("start_dial_session", { p_filter: filter }),

    async endSession(sessionId) {
      await rpc("end_dial_session", { p_session_id: sessionId });
    },

    async pauseSession(sessionId, paused) {
      await rpc("pause_dial_session", {
        p_session_id: sessionId,
        p_paused: paused,
      });
    },

    async claimNext(sessionId, exclude = []) {
      const data = await rpc<ClaimRow[]>("claim_next_lead", {
        p_session_id: sessionId,
        ...(exclude.length ? { p_exclude: [...exclude] } : {}),
      });
      return toResult(firstRow(data), sessionId);
    },

    async claimSpecific(sessionId, companyId, target) {
      const data = target
        ? await rpc<ClaimRow[]>("claim_contact_call", {
            p_session_id: sessionId,
            p_company_id: companyId,
            p_contact_id: target.contactId,
            p_phone: target.phone,
          })
        : await rpc<ClaimRow[]>("claim_specific_lead", {
            p_session_id: sessionId,
            p_company_id: companyId,
          });
      // The RPC may have started a session (sessionId null): report the live one.
      const session = sessionId ?? (await getActiveSession())?.id;
      if (session == null) throw new DialerApiError("session_not_active");
      const row = firstRow(data);
      if (row?.reason === "locked")
        return {
          ...(await toResult(row, session)),
          locked_by_name: await lockedByName(companyId),
        };
      const result = await toResult(row, session, () =>
        leadTimezone(companyId),
      );
      // The contact's number is the one claim_contact_call validated and recorded on the call row (review fix M1).
      if (target && result.reason === "ok")
        return {
          ...result,
          contact: await calledContact(
            target.contactId,
            result.to_number ?? target.phone,
          ),
        };
      return result;
    },

    renewLease: (companyId) =>
      rpc<string>("renew_lease", { p_company_id: companyId }),

    async releaseLease(companyId) {
      await rpc("release_lease", { p_company_id: companyId });
    },

    // GAVION small fixes: the softphone launch time (52_gavion_small_fixes.sql)
    markCallLaunched: (callId) =>
      rpc<string>("mark_call_launched", { p_call_id: callId }),

    async logOutcome(p) {
      // Read before the RPC: the demo-deal prompt and the "skip deleted the call" signal depend on it.
      const prev = must(
        await db()
          .from("calls")
          .select("outcome, outcome_version, voipms_cdr_id, deal_id")
          .eq("id", p.call_id)
          .maybeSingle(),
      ) as Row | null;
      const call = await rpc<CallRow>("log_call_outcome", { p_call: p });
      const applied = !prev || p.version > Number(prev.outcome_version ?? 0);
      const first = !prev?.outcome || prev.outcome === "unlogged";
      const deleted =
        applied && p.outcome === "skipped" && first && !prev?.voipms_cdr_id;
      const demoDeal =
        applied &&
        prev?.outcome === "demo_booked" &&
        p.outcome !== "demo_booked";
      return {
        call: deleted ? null : call,
        demo_deal_id: demoDeal
          ? ((call?.deal_id ?? prev?.deal_id ?? null) as number | null)
          : null,
      };
    },

    // GAVION off-CRM call (SQL public.log_off_crm_call, 86_)
    logOffCrmCall: (p) =>
      rpc<CallRow>("log_off_crm_call", {
        p_company_id: p.company_id,
        p_contact_id: p.contact_id,
        p_called_at: p.called_at,
        p_call: p.call,
      }),

    // GAVION skip-callback (SQL public.skip_callback, 93_)
    async skipCallback(companyId, reason) {
      await rpc("skip_callback", {
        p_company_id: companyId,
        p_reason: reason,
      });
    },

    async queuePreview(limit, exclude = []) {
      // my_queue_preview has no exclusion: ask for enough rows to drop the skipped ones (max 50).
      const rows = await rpc<{ company_id: number }[]>("my_queue_preview", {
        p_limit: Math.min(limit + exclude.length, 50),
      });
      return loadSnapshots(
        db(),
        (rows ?? [])
          .map((r) => r.company_id)
          .filter((id) => !exclude.includes(id))
          .slice(0, limit),
      );
    },

    // GAVION callbacks today
    async callbacksLaterToday() {
      const rows = await rpc<
        { company_id: number; name: string; callback_at: string }[] | null
      >("my_callbacks_later_today");
      return (rows ?? []).map((r) => ({
        companyId: r.company_id,
        name: r.name,
        at: r.callback_at,
      }));
    },

    async queueFilterOptions() {
      const rows = await rpc<QueueFilterOption[] | null>(
        "my_queue_filter_options",
      );
      return (rows ?? []).map((r) => ({
        kind: r.kind,
        value: r.value,
        callable_count: Number(r.callable_count) || 0,
      }));
    },

    todayStats: async () =>
      ({
        dials: 0,
        contacts: 0,
        demos: 0,
        ...(await rpc<TodayStats | null>("my_dial_stats_today")),
      }) as TodayStats,

    async todoCalls({ allReps, salesId }) {
      return (
        (await rpc<CallRow[]>("todo_calls", {
          p_all_reps: allReps,
          p_sales_id: salesId ?? null,
        })) ?? []
      );
    },

    // GAVION my-calls: « Mes appels ». Calls RLS lets every active rep read every call, so a rep's own-calls scope is
    // this sales_id filter (calls are team-visible elsewhere too); `allReps` is only ever set by the admin UI.
    async callHistory({
      allReps,
      salesId,
      from,
      to,
      outcomes,
      direction,
      page,
      perPage,
    }) {
      let q = db()
        .from("calls")
        .select(
          "id, company_id, contact_id, sales_id, direction, dialed_at, outcome, note, callback_at, duration_s, " +
            "companies(name), contacts(first_name, last_name)",
          { count: "exact" },
        )
        .gte("dialed_at", from)
        .lt("dialed_at", to);
      if (!allReps) q = q.eq("sales_id", me);
      else if (salesId != null) q = q.eq("sales_id", salesId);
      if (direction) q = q.eq("direction", direction);
      if (outcomes.length) {
        const codes = outcomes.filter((o) => o !== "none");
        const parts = codes.length ? [`outcome.in.(${codes.join(",")})`] : [];
        if (outcomes.includes("none"))
          parts.push("outcome.is.null", "outcome.eq.unlogged");
        q = q.or(parts.join(","));
      }
      const res = await q
        .order("dialed_at", { ascending: false })
        .order("id", { ascending: false })
        .range((page - 1) * perPage, page * perPage - 1);
      // A page past the end (rows deleted meanwhile): PostgREST answers 416 / PGRST103; the page goes back to 1.
      if ((res.error as { code?: string } | null)?.code === "PGRST103")
        return { rows: [], total: 0 };
      const rows = (must(res) ?? []) as unknown as Row[];
      const one = (v: unknown): Row | null =>
        ((Array.isArray(v) ? v[0] : v) ?? null) as Row | null;
      return {
        total: res.count ?? rows.length,
        rows: rows.map((r): CallHistoryRow => {
          const k = one(r.contacts);
          return {
            id: Number(r.id),
            company_id: r.company_id == null ? null : Number(r.company_id),
            contact_id: r.contact_id == null ? null : Number(r.contact_id),
            sales_id: Number(r.sales_id),
            direction: r.direction === "inbound" ? "inbound" : "outbound",
            dialed_at: String(r.dialed_at),
            outcome: (r.outcome ?? null) as CallHistoryRow["outcome"],
            note: str(r.note),
            callback_at: str(r.callback_at),
            duration_s: r.duration_s == null ? null : Number(r.duration_s),
            company_name: str(one(r.companies)?.name),
            contact_name: k
              ? contactDisplayName({
                  first_name: str(k.first_name),
                  last_name: str(k.last_name),
                }) || null
              : null,
          };
        }),
      };
    },

    async recentActivity(companyId, limit) {
      const calls = (must(
        await db()
          .from("calls")
          .select("id, dialed_at, outcome, note")
          .eq("company_id", companyId)
          .not("outcome", "is", null)
          .order("dialed_at", { ascending: false })
          .limit(limit),
      ) ?? []) as Row[];
      const notes = (must(
        await db()
          .from("company_notes")
          .select("id, date, text")
          .eq("company_id", companyId)
          .order("date", { ascending: false })
          .limit(limit),
      ) ?? []) as Row[];
      const items: ActivityItem[] = [
        ...calls.map((c) => ({
          kind: "call" as const,
          id: Number(c.id),
          at: String(c.dialed_at),
          title: String(c.outcome),
          body: str(c.note),
        })),
        ...notes.map((n) => ({
          kind: "note" as const,
          id: Number(n.id),
          at: String(n.date),
          title: "note",
          body: str(n.text),
        })),
      ];
      return items
        .sort((a, b) => Date.parse(b.at) - Date.parse(a.at))
        .slice(0, limit);
    },

    async deleteDeal(dealId) {
      // RLS: admins only. A refused delete is not an error in PostgREST, just zero rows.
      const rows = must(
        await db().from("deals").delete().eq("id", dealId).select("id"),
      ) as Row[] | null;
      if (!rows?.length) throw new DialerApiError("forbidden");
    },

    async getMyPrefs() {
      const row = must(
        await db().from("sales").select("prefs").eq("id", me).single(),
      ) as { prefs?: SalesDialPrefs | null } | null;
      return { ...(row?.prefs ?? {}) };
    },

    async saveMyPrefs(patch) {
      return {
        ...((await rpc<SalesDialPrefs | null>("set_my_dial_prefs", {
          p_prefs: patch,
        })) ?? {}),
      };
    },

    // GAVION X4: daily digest opt-out
    async setMyDigestEnabled(enabled) {
      return {
        ...((await rpc<MyPrefs | null>("set_my_digest_enabled", {
          p_enabled: enabled,
        })) ?? {}),
      };
    },

    // GAVION rep-missed-call: the rep's own « Appel manqué » email switch
    async setMyMissedCallEmail(enabled) {
      return {
        ...((await rpc<MyPrefs | null>("set_my_missed_call_email", {
          p_enabled: enabled,
        })) ?? {}),
      };
    },

    // GAVION inbound calls (80_gavion_inbound_calls.sql)
    startInboundCall: ({ phone, companyId, contactId }) =>
      rpc<InboundStartResult>("start_inbound_call", {
        p_phone: phone,
        p_company_id: companyId ?? null,
        p_contact_id: contactId ?? null,
      }),
    endInboundCall: (phone) =>
      rpc<InboundEndResult>("end_inbound_call", { p_phone: phone }),
  };
  return api;
}
